import styled from "@emotion/styled";
import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import { TRANSFORMERS } from "@lexical/markdown";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { CircularProgress } from "@mui/material";
import { $createParagraphNode, $getRoot, $insertNodes } from "lexical";
import { createContext, useContext } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
	PrimaryButton,
	TextPrimaryButton,
} from "src/components/Default/LekUI/Button";
import CodeHighlightPlugin from "src/components/TextEditor/plugins/CodeHighlightPlugin";
import ListMaxIndentLevelPlugin from "src/components/TextEditor/plugins/ListMaxIndentLevelPlugin";
import ToolbarPlugin from "src/components/TextEditor/plugins/ToolbarPlugin";
import ExampleTheme from "src/components/TextEditor/themes/ExampleTheme";
import formatHtmlTag from "src/helpers/formatHtmlTag";

export const appendHtmlToEditor = (editor, htmlNowy) => {
	if (!htmlNowy) {
		return;
	}

	editor.update(
		() => {
			const parser = new DOMParser();
			const dom = parser.parseFromString(String(htmlNowy), "text/html");
			const nodes = $generateNodesFromDOM(editor, dom);

			const root = $getRoot();
			root.selectEnd();

			$insertNodes(nodes);

			root.selectEnd();
		},
		{ tag: "append-html-from-formik" },
	);
};

function Placeholder({ placeholder }) {
	const { t } = useTranslation();

	return (
		<div className="editor-placeholder">
			{placeholder ?? t("exam.comments.addComment")}
		</div>
	);
}

export const Loading = styled(({ className, children, size }) => {
	return (
		<div className={className}>
			<div>
				<CircularProgress size={size ?? 40} />
			</div>
			{children}
		</div>
	);
})`
  position: relative;

  > div:first-of-type {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.2);
    z-index: 9;
    border-radius: 12px;
    display: ${(props) => (props.isLoading ? "flex" : "none")};
    align-items: center;
    justify-content: center;
  }
`;

const editorConfig = {
	// The editor theme
	theme: ExampleTheme,
	// Handling of errors during update
	onError(error) {
		throw error;
	},
	// Any custom nodes go here
	nodes: [
		HeadingNode,
		ListNode,
		ListItemNode,
		QuoteNode,
		CodeNode,
		CodeHighlightNode,
		TableNode,
		TableCellNode,
		TableRowNode,
		AutoLinkNode,
		LinkNode,
	],
};

const EMPTY_CONTENT =
	'{"root":{"children":[{"children":[],"direction":null,"format":"","indent":0,"type":"paragraph","version":1}],"direction":null,"format":"","indent":0,"type":"root","version":1}}';

const SubmitContainer = ({
	onSubmit,
	clearAfterSave,
	disabled,
	submitText,
	cancelText,
	onCancel,
}) => {
	const { t } = useTranslation();
	const [editor] = useLexicalComposerContext();

	const submit = () => {
		editor.update(async () => {
			const htmlString = $generateHtmlFromNodes(editor, null);
			if (formatHtmlTag(htmlString).trim() === "" && disabled) {
				toast.error(t("errors.emptyComment"), {
					position: toast.POSITION.BOTTOM_LEFT,
					pauseOnHover: false,
					autoClose: 5000,
				});
			} else {
				try {
					await onSubmit(htmlString);
					clearAfterSave &&
						editor.setEditorState(editor.parseEditorState(EMPTY_CONTENT));
				} catch {
					// nothing here
				}
			}
		});
	};

	return (
		<div className="flex items-start justify-end mt-12 gap-8">
			{cancelText && (
				<TextPrimaryButton onClick={onCancel}>{cancelText}</TextPrimaryButton>
			)}
			<PrimaryButton onClick={submit}>
				{submitText || t("commands.add")}
			</PrimaryButton>
		</div>
	);
};

const EditorContainer = ({ children }) => {
	return <div className="editor-container grey-box">{children}</div>;
};

export function createInitialEditorState(htmlString) {
	if (!htmlString) {
		return () => {
			$getRoot().append($createParagraphNode());
		};
	}

	return (editor) => {
		const parser = new DOMParser();
		const dom = parser.parseFromString(htmlString, "text/html");
		const nodes = $generateNodesFromDOM(editor, dom);
		$getRoot().clear();
		$insertNodes(nodes);
	};
}

// false = edytor nie łapie focusu po zamontowaniu (np. wiele edytorów naraz na liście w adminie)
export const EditorAutoFocusContext = createContext(true);

export default function Editor({
	onSubmit,
	clearAfterSave,
	openUploadModal,
	gallery,
	disabled,
	isLoading,
	placeholder,
	submitText,
	cancelText,
	onCancel,
	initialHtml = "",
	additionalPlugins = null,
	className,
}) {
	const initialEditorState = createInitialEditorState(initialHtml);
	const autoFocus = useContext(EditorAutoFocusContext);

	return (
		<Loading isLoading={isLoading} className={className}>
			<LexicalComposer
				initialConfig={{
					...editorConfig,
					editorState: initialEditorState,
				}}
			>
				<EditorContainer className="editor-container">
					<ToolbarPlugin
						openUploadModal={openUploadModal}
						additionalPlugins={additionalPlugins}
					/>
					<div className="editor-inner">
						<RichTextPlugin
							contentEditable={<ContentEditable className="editor-input" />}
							placeholder={<Placeholder placeholder={placeholder} />}
							ErrorBoundary={LexicalErrorBoundary}
						/>
						<HistoryPlugin />
						{autoFocus && <AutoFocusPlugin />}
						<CodeHighlightPlugin />
						<ListPlugin />
						<LinkPlugin />
						<ListMaxIndentLevelPlugin maxDepth={7} />
						<MarkdownShortcutPlugin transformers={TRANSFORMERS} />
					</div>
					{gallery}
				</EditorContainer>
				<SubmitContainer
					onSubmit={onSubmit}
					clearAfterSave={clearAfterSave}
					disabled={disabled}
					submitText={submitText}
					cancelText={cancelText}
					onCancel={onCancel}
				/>
			</LexicalComposer>
		</Loading>
	);
}
